/*
 * The simulated corridor behind the 123BIMLive analyses story: one two-lane rural highway
 * with a place for every RSA 3D analysis, and those analyses run on the same geometry the
 * points are drawn from. It is not a real road, and the page says so; the checks and their
 * numbers are real arithmetic on it.
 *
 * Units are metres. `s` is the station along the centreline, `o` the offset from it (right
 * positive), `y` height. Criteria are AASHTO's Green Book (2018) and Roadside Design Guide,
 * and the MUTCD, at a 55 mph design speed.
 */

export const FT = 3.28084;
export const LENGTH = 1400;
export const LANE = 3.6; // one lane: the edge of travelled way is at |o| = LANE
export const SHOULDER = 6.0; // paved shoulder out to |o| = 6

export const STANDARDS = {
  speedMph: 55,
  ssdFt: 495, // Green Book Table 3-1, stopping sight distance
  psdFt: 900, // MUTCD Table 3B-1, minimum passing sight distance
  isdFt: 610, // Green Book Table 9-7, left turn from stop, passenger car
  eye: 1.08, // 3.5 ft driver's eye
  object: 0.6, // 2.0 ft object in the road
  vehicle: 1.08, // 3.5 ft oncoming vehicle
  clearZoneFt: 22, // Roadside Design Guide Table 3-1: 20-24 ft at 55 mph, 1,500-6,000 ADT
  superelevation: 0.06,
  sideFriction: 0.15,
  warrantHeightFt: 10, // fill over 10 ft on a slope steeper than 1V:3H warrants barrier
};

/** Where each feature sits, by station. */
export const SITE = {
  crest: 160,
  passing: [240, 400], // dashed centreline: passing allowed
  intersection: 450, // T-intersection, side road to the right
  curve: { spiralIn: [520, 560], arc: [560, 720], spiralOut: [720, 760], radius: 180 }, // left
  embankment: [950, 1100], // 6 m fill on the right, tapered over 30 m at each end
  guardrail: [980, 1060],
  clearZone: [1110, 1260],
  sectionsEvery: 15, // embankment cross-sections
};

const DS = 0.5;
const SAMPLES = LENGTH / DS + 1;
const lerp = (a, b, t) => a + (b - a) * t;
const clamp01 = (t) => Math.max(0, Math.min(1, t));
const ramp = (s, a, b, from, to) =>
  s <= a ? from : s >= b ? to : lerp(from, to, (s - a) / (b - a));
const smooth = (t) => t * t * (3 - 2 * t);

export function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- alignment ------------------------------------------------------------------------

/** Curvature, 1/m: tangent, spiral in, a 180 m arc to the left, spiral out. */
function curvature(s) {
  const { spiralIn, arc, spiralOut, radius } = SITE.curve;
  const k = -1 / radius;
  if (s < spiralIn[0] || s > spiralOut[1]) return 0;
  if (s < arc[0]) return (k * (s - spiralIn[0])) / (spiralIn[1] - spiralIn[0]);
  if (s <= arc[1]) return k;
  return (k * (spiralOut[1] - s)) / (spiralOut[1] - spiralOut[0]);
}

const bump = (s, c, h, w) => h * Math.exp(-(((s - c) / w) ** 2));
/** Vertical profile: the crest, rolling grade, and a level spot at the curve's entry. */
function profile(s) {
  return bump(s, SITE.crest, 7, 48) + bump(s, 525, 1.4, 60) + 0.9 * Math.sin(s / 210);
}

const E = STANDARDS.superelevation;
/** Right-lane cross slope (y change per metre outward): the curve's outside lane. */
function slopeRight(s) {
  if (s < 500 || s > 780) return -0.02; // normal crown
  if (s < 520) return ramp(s, 500, 520, -0.02, 0); // tangent runout: through level
  if (s < 540) return ramp(s, 520, 540, 0, 0.02);
  if (s < 560) return ramp(s, 540, 560, 0.02, E);
  if (s <= 720) return E;
  if (s < 740) return ramp(s, 720, 740, E, 0.02);
  if (s < 760) return ramp(s, 740, 760, 0.02, 0);
  return ramp(s, 760, 780, 0, -0.02);
}
/** Left-lane cross slope: keeps its crown until the whole section rotates. */
function slopeLeft(s) {
  if (s < 540 || s > 740) return -0.02;
  if (s < 560) return ramp(s, 540, 560, -0.02, -E);
  if (s <= 720) return -E;
  return ramp(s, 720, 740, -E, -0.02);
}

export function buildAlignment() {
  const X = new Float64Array(SAMPLES);
  const Z = new Float64Array(SAMPLES);
  const H = new Float64Array(SAMPLES);
  let h = 0;
  let x = 0;
  let z = 0;
  for (let i = 0; i < SAMPLES; i++) {
    X[i] = x;
    Z[i] = z;
    H[i] = h;
    const k = curvature(i * DS + DS / 2);
    const hm = h + (k * DS) / 2;
    x += Math.sin(hm) * DS;
    z += Math.cos(hm) * DS;
    h += k * DS;
  }
  return { X, Z, H };
}

/** Centreline point, heading, height and cross slopes at station s. */
export function sample(al, s) {
  const f = Math.max(0, Math.min(SAMPLES - 1.0001, s / DS));
  const i = Math.floor(f);
  const t = f - i;
  const cs = Math.max(0, Math.min(LENGTH, s));
  return {
    x: lerp(al.X[i], al.X[i + 1], t),
    z: lerp(al.Z[i], al.Z[i + 1], t),
    h: lerp(al.H[i], al.H[i + 1], t),
    y: profile(cs),
    sr: slopeRight(cs),
    sl: slopeLeft(cs),
  };
}

/** Grade (rise over run) at station s. */
export const gradeAt = (s) => (profile(s + 0.5) - profile(s - 0.5)) / 1;
export const crossSlopeRight = slopeRight;

/** World x, z of (s, o). Heading h: forward is (sin h, cos h), right is (cos h, -sin h). */
export function toWorld(al, s, o) {
  const p = sample(al, s);
  return { x: p.x + Math.cos(p.h) * o, z: p.z - Math.sin(p.h) * o, h: p.h };
}

/** Station and offset of a world point, searched near a guessed station. */
export function locate(al, x, z, guess, window = 12) {
  let best = guess;
  let bestD = Infinity;
  const lo = Math.max(0, guess - window);
  const hi = Math.min(LENGTH, guess + window);
  for (let s = lo; s <= hi; s += 1) {
    const p = sample(al, s);
    const d = (x - p.x) ** 2 + (z - p.z) ** 2;
    if (d < bestD) {
      bestD = d;
      best = s;
    }
  }
  const p = sample(al, best);
  const dx = x - p.x;
  const dz = z - p.z;
  return {
    s: best + dx * Math.sin(p.h) + dz * Math.cos(p.h),
    o: dx * Math.cos(p.h) - dz * Math.sin(p.h),
  };
}

// ---- cross-section ------------------------------------------------------------------------

/** Fill height of the right-side embankment, m. */
export function embankment(s) {
  const [a, b] = SITE.embankment;
  if (s <= a || s >= b) return 0;
  return 6 * smooth(Math.min(clamp01((s - a) / 30), clamp01((b - s) / 30)));
}

function terrainNoise(s, o) {
  return (
    0.35 * Math.sin(s / 37 + o / 23) +
    0.25 * Math.sin(s / 71 - o / 41) +
    0.18 * Math.sin(o / 9 + s / 13)
  );
}

/** Height of the pavement or ground at (s, o). */
export function heightAt(al, s, o) {
  const p = sample(al, s);
  const pave = (oo) => p.y + (oo >= 0 ? p.sr * oo : p.sl * -oo);
  const a = Math.abs(o);
  if (a <= SHOULDER) return pave(o);
  const side = o >= 0 ? 1 : -1;
  const edge = pave(side * SHOULDER);
  const d = a - SHOULDER;
  // Where the side road meets the highway there is no ditch: road and apron stay level.
  if (side > 0 && Math.abs(s - SITE.intersection) < 9) return edge - 0.004 * d;
  // Ditch: a 1V:4H foreslope to 1 m deep, then back up to rolling ground.
  const ditch = d <= 4 ? -d / 4 : d <= 7 ? -1 + (d - 4) / 3 : 0;
  const far = d > 7 ? 0.015 * (d - 7) + terrainNoise(s, o) * Math.min(1, (d - 7) / 6) : 0;
  let y = edge + ditch + far;
  const fill = side > 0 ? embankment(s) : 0;
  if (fill > 0) {
    // A 1V:2H fill slope down to the toe, then the ground below.
    const toe = 2 * fill;
    const below = d > toe ? terrainNoise(s, o) * 0.3 * Math.min(1, (d - toe) / 6) : 0;
    y = Math.min(y, edge - Math.min(d / 2, fill) + below);
  }
  return y;
}

// ---- the point cloud ------------------------------------------------------------------------

/** Point kinds, as the scene colours them. */
export const KIND = {
  SURFACE: 0,
  EDGE_LINE: 1,
  CENTRE_LINE: 2,
  GROUND: 3,
  CANOPY: 4,
  TRUNK: 5,
  POLE: 6,
  WIRE: 7,
  SIGN_POST: 8,
  SIGN_FACE: 9,
  GUARDRAIL: 10,
  SIDE_ROAD: 11,
  SIDE_MARK: 12,
};
/** Values per point in `points`: x, y, z, intensity, kind, s, o, id. */
export const STRIDE = 8;

/** The signs on the corridor, MUTCD codes. `face`: "road" faces oncoming traffic. */
export const SIGNS = [
  {
    code: "R2-1",
    label: "Speed Limit 55",
    s: 250,
    o: 7.4,
    w: 0.6,
    h: 0.75,
    cy: 2.3,
    shape: "rect",
  },
  {
    code: "R1-1",
    label: "Stop",
    s: 446.2,
    o: 10.6,
    w: 0.76,
    h: 0.76,
    cy: 2.3,
    shape: "octagon",
    face: "side",
  },
  {
    code: "W1-2L",
    label: "Curve left",
    s: 505,
    o: 7.4,
    w: 0.9,
    h: 0.9,
    cy: 2.75,
    shape: "diamond",
  },
  {
    code: "W13-1P",
    label: "Advisory 40 mph",
    s: 505,
    o: 7.4,
    w: 0.45,
    h: 0.45,
    cy: 1.95,
    shape: "rect",
  },
  { code: "W1-8", label: "Chevron", s: 585, o: 7.4, w: 0.45, h: 0.6, cy: 1.6, shape: "rect" },
  { code: "W1-8", label: "Chevron", s: 625, o: 7.4, w: 0.45, h: 0.6, cy: 1.6, shape: "rect" },
  { code: "W1-8", label: "Chevron", s: 665, o: 7.4, w: 0.45, h: 0.6, cy: 1.6, shape: "rect" },
  { code: "W1-8", label: "Chevron", s: 705, o: 7.4, w: 0.45, h: 0.6, cy: 1.6, shape: "rect" },
];

function inShape(shape, u, v) {
  // u, v in [-1, 1] across the plate.
  if (shape === "diamond") return Math.abs(u) + Math.abs(v) <= 1;
  if (shape === "octagon")
    return Math.abs(u) <= 1 && Math.abs(v) <= 1 && Math.abs(u) + Math.abs(v) <= 1.41;
  return true;
}

function placeTrees(rand) {
  const trees = [];
  const add = (s, o, scale = 1) =>
    trees.push({
      s,
      o,
      trunk: (2 + rand() * 1.5) * scale,
      r: (2 + rand() * 1.6) * scale,
      top: (7 + rand() * 6) * scale,
    });
  // Brush: foliage from the ground up, low enough to hide a car from a driver's eye.
  const brush = (s, o) =>
    trees.push({ s, o, trunk: 0.15, r: 1.6 + rand() * 0.8, top: 2.2 + rand() * 0.6, brush: true });
  const [cz0, cz1] = SITE.clearZone;
  for (let s = 4; s < LENGTH; s += 6 + rand() * 5) {
    for (const side of [-1, 1]) {
      if (rand() < 0.28) continue;
      let o = side * (13 + Math.pow(rand(), 0.9) * 42);
      if (side > 0 && Math.abs(s - SITE.intersection) < 14) continue; // the side road
      if (side > 0 && embankment(s) > 0.5) o = side * (2 * embankment(s) + 8 + rand() * 30);
      if (side > 0 && s > cz0 - 10 && s < cz1 + 10 && o < 16) o = 16 + rand() * 30;
      add(s, o);
    }
  }
  // Inside of the curve: the woods a passing driver's view has to cut across.
  for (let s = 548; s < 720; s += 7 + rand() * 4) add(s, -(8.5 + rand() * 6));
  // Brush on the intersection's corner, in the way of the view up the road.
  for (const [s, o] of [
    [459, 7.8],
    [464, 8.3],
    [469, 7.6],
    [474, 8.8],
  ])
    brush(s, o);
  // Inside the clear zone (22 ft from the edge line): fixed objects a car could hit.
  for (const [s, o] of [
    [1128, 9.2],
    [1172, 9.9],
    [1226, 9.5],
  ])
    add(s, o, 0.9);
  return trees.sort((a, b) => a.s - b.s);
}

function placePoles() {
  const poles = [];
  for (let s = 20; s < LENGTH; s += 45) {
    if (Math.abs(s - SITE.intersection) < 14) continue;
    const inZone = s > SITE.clearZone[0] && s < SITE.clearZone[1];
    const onFill = embankment(s) > 0.5;
    poles.push({ s, o: inZone ? 9.6 : onFill ? 2 * embankment(s) + 9 : 14, top: 9.5 });
  }
  return poles;
}

/**
 * The corridor without its points: alignment, trees, poles and signs. The analyses run on
 * this, so their results never depend on how densely the scene is drawn.
 */
export function buildLayout() {
  const al = buildAlignment();
  return { al, trees: placeTrees(rng(1905)), poles: placePoles(), signs: SIGNS };
}

/**
 * Builds the corridor's points (Float32Array, STRIDE values each, sorted by station) on
 * top of its layout. `density` scales point counts (lower on small screens).
 */
export function buildWorld({ density = 1 } = {}) {
  const layout = buildLayout();
  const { al } = layout;
  const rand = rng(20260924);
  const buckets = Array.from({ length: LENGTH }, () => []);
  const put = (s, o, y, intensity, kind, id = -1) => {
    const b = Math.max(0, Math.min(LENGTH - 1, Math.floor(s)));
    const w = toWorld(al, s, o);
    buckets[b].push(w.x, y, w.z, intensity, kind, s, o, id);
  };
  const count = (n) => Math.max(0, Math.round(n * density + rand() - 0.5));

  for (let s0 = 0; s0 < LENGTH; s0++) {
    // Pavement and paved shoulders.
    for (let n = count(26); n > 0; n--) {
      const s = s0 + rand();
      const o = (rand() * 2 - 1) * SHOULDER;
      const shoulder = Math.abs(o) > LANE;
      put(
        s,
        o,
        heightAt(al, s, o),
        shoulder ? 0.22 + rand() * 0.14 : 0.28 + rand() * 0.18,
        KIND.SURFACE
      );
    }
    // Ground: dense near the road, as a mobile scan is, sparser farther out.
    for (let n = count(42); n > 0; n--) {
      const s = s0 + rand();
      const side = rand() < 0.5 ? -1 : 1;
      const o = side * (SHOULDER + Math.pow(rand(), 1.9) * 54);
      if (side > 0 && Math.abs(s - SITE.intersection) < 3.3 && o < 150) continue; // side road
      put(s, o, heightAt(al, s, o), 0.12 + rand() * 0.24, KIND.GROUND);
    }
  }

  // Markings: edge lines, and the centre line (dashed where passing is allowed).
  const step = 0.3 / density;
  for (let s = 0; s < LENGTH; s += step) {
    for (const o of [-3.66, -3.52, 3.52, 3.66]) {
      put(s, o, heightAt(al, s, o) + 0.005, 0.84 + rand() * 0.16, KIND.EDGE_LINE);
    }
    const passing = s >= SITE.passing[0] && s <= SITE.passing[1];
    if (passing) {
      if ((s - SITE.passing[0]) % 12 < 3) {
        for (const o of [-0.06, 0.06])
          put(s, o, heightAt(al, s, o) + 0.005, 0.8 + rand() * 0.15, KIND.CENTRE_LINE);
      }
    } else {
      for (const o of [-0.16, -0.06, 0.06, 0.16]) {
        put(s, o, heightAt(al, s, o) + 0.005, 0.8 + rand() * 0.15, KIND.CENTRE_LINE);
      }
    }
  }

  // The side road: 6.6 m wide, running off to the right, with its stop bar and centre line.
  {
    const at = SITE.intersection;
    const base = heightAt(al, at, SHOULDER);
    for (let n = count(2000); n > 0; n--) {
      const t = (rand() * 2 - 1) * 3.3;
      const o = SHOULDER + rand() * 144;
      put(at + t, o, base - 0.004 * (o - SHOULDER), 0.26 + rand() * 0.18, KIND.SIDE_ROAD);
    }
    for (let o = 8.2; o < 150; o += step) {
      const y = base - 0.004 * (o - SHOULDER) + 0.005;
      put(at, o, y, 0.82 + rand() * 0.15, KIND.SIDE_MARK);
    }
    for (let t = -3.3; t <= 0; t += step / 2) {
      for (const o of [8.0, 8.2, 8.4])
        put(at + t, o, base - 0.009, 0.9 + rand() * 0.1, KIND.SIDE_MARK);
    }
  }

  // Trees: a trunk and a canopy.
  const { trees, poles } = layout;
  trees.forEach((tree, id) => {
    const g = heightAt(al, tree.s, tree.o);
    for (let n = count(12); n > 0; n--) {
      const a = rand() * Math.PI * 2;
      put(
        tree.s + Math.sin(a) * 0.15,
        tree.o + Math.cos(a) * 0.15,
        g + rand() * tree.trunk,
        0.3 + rand() * 0.2,
        KIND.TRUNK,
        id
      );
    }
    const cy = g + tree.trunk + (tree.top - tree.trunk) / 2;
    const rv = (tree.top - tree.trunk) / 2;
    for (let n = count(95); n > 0; n--) {
      const u = rand() * 2 - 1;
      const phi = rand() * Math.PI * 2;
      const k = Math.pow(rand(), 0.35);
      const ring = Math.sqrt(1 - u * u) * k;
      put(
        tree.s + Math.sin(phi) * ring * tree.r,
        tree.o + Math.cos(phi) * ring * tree.r,
        cy + u * k * rv,
        0.28 + rand() * 0.34,
        KIND.CANOPY,
        id
      );
    }
  });

  // Utility poles, and the wires between them.
  poles.forEach((pole, id) => {
    const g = heightAt(al, pole.s, pole.o);
    pole.base = g;
    for (let y = 0; y < pole.top; y += 0.16 / density)
      put(pole.s, pole.o, g + y, 0.46 + rand() * 0.2, KIND.POLE, id);
    for (let d = -1; d <= 1; d += 0.1 / density)
      put(pole.s, pole.o + d, g + pole.top - 0.4, 0.5, KIND.POLE, id);
  });
  for (let i = 0; i + 1 < poles.length; i++) {
    const a = poles[i];
    const b = poles[i + 1];
    for (const dw of [-0.8, 0.8]) {
      for (let t = 0; t <= 1; t += 0.012 / density) {
        const s = lerp(a.s, b.s, t);
        const o = lerp(a.o, b.o, t) + dw;
        const y = lerp(a.base, b.base, t) + a.top - 0.35 - Math.sin(Math.PI * t) * 0.9;
        put(s, o, y, 0.55, KIND.WIRE, i);
      }
    }
  }

  // Signs: a post, and a retroreflective face (the brightest returns in a scan).
  SIGNS.forEach((sign, id) => {
    const g = heightAt(al, sign.s, sign.o);
    sign.base = g;
    const bottom = sign.cy - sign.h / 2;
    if (sign.code !== "W13-1P") {
      for (let y = 0; y < bottom; y += 0.06 / density)
        put(sign.s, sign.o, g + y, 0.5, KIND.SIGN_POST, id);
    }
    const gridStep = 0.045 / Math.sqrt(density);
    for (let u = -1; u <= 1; u += (2 * gridStep) / sign.w) {
      for (let v = -1; v <= 1; v += (2 * gridStep) / sign.h) {
        if (!inShape(sign.shape, u, v)) continue;
        const y = g + sign.cy + (v * sign.h) / 2;
        if (sign.face === "side")
          put(sign.s + (u * sign.w) / 2, sign.o, y, 0.95, KIND.SIGN_FACE, id);
        else put(sign.s, sign.o + (u * sign.w) / 2, y, 0.95, KIND.SIGN_FACE, id);
      }
    }
  });

  // Guardrail on the fill: posts every 6 ft 3 in, and the W-beam.
  const [g0, g1] = SITE.guardrail;
  for (let s = g0; s <= g1; s += 1.905) {
    const g = heightAt(al, s, 6.35);
    for (let y = 0; y < 0.75; y += 0.08 / density) put(s, 6.35, g + y, 0.7, KIND.GUARDRAIL);
  }
  for (let s = g0; s <= g1; s += 0.1 / density) {
    const g = heightAt(al, s, 6.2);
    for (const y of [0.55, 0.63, 0.71]) put(s, 6.2, g + y, 0.8 + rand() * 0.1, KIND.GUARDRAIL);
  }

  // Flatten, keeping station order, with where each metre starts.
  let total = 0;
  const start = new Uint32Array(LENGTH + 1);
  for (let b = 0; b < LENGTH; b++) {
    start[b] = total;
    total += buckets[b].length / STRIDE;
  }
  start[LENGTH] = total;
  const points = new Float32Array(total * STRIDE);
  let at = 0;
  for (const bucket of buckets) {
    points.set(bucket, at);
    at += bucket.length;
  }
  return { ...layout, points, start, count: total };
}

// ---- the analyses ------------------------------------------------------------------------

function treeIndex(trees) {
  const byMetre = Array.from({ length: LENGTH }, () => []);
  trees.forEach((t, i) => {
    for (let s = Math.floor(t.s - t.r); s <= Math.ceil(t.s + t.r); s++) {
      if (s >= 0 && s < LENGTH) byMetre[s].push(i);
    }
  });
  return byMetre;
}

/**
 * The first thing in the way of a sight line from a to b ({ s, o, y } with y absolute),
 * or null. What blocks it: the road itself over a crest, the ground, a tree or a pole.
 */
export function lineOfSight(world, a, b) {
  const { al } = world;
  world.treeIndex ||= treeIndex(world.trees);
  const A = toWorld(al, a.s, a.o);
  const B = toWorld(al, b.s, b.o);
  const len = Math.hypot(B.x - A.x, B.z - A.z);
  const steps = Math.max(6, Math.ceil(len / 3));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const x = lerp(A.x, B.x, t);
    const z = lerp(A.z, B.z, t);
    const y = lerp(a.y, b.y, t);
    const { s, o } = locate(al, x, z, lerp(a.s, b.s, t));
    if (s < 0 || s > LENGTH) continue;
    if (heightAt(al, s, o) > y) {
      return { s, o, y, by: Math.abs(o) <= SHOULDER ? "Vertical curvature" : "Terrain" };
    }
    for (const ti of world.treeIndex[Math.floor(s)] || []) {
      const tree = world.trees[ti];
      const g = heightAt(al, tree.s, tree.o);
      const tp = toWorld(al, tree.s, tree.o);
      const d = Math.hypot(x - tp.x, z - tp.z);
      if (y > g + tree.trunk && y < g + tree.top && d < tree.r * 0.85)
        return { s, o, y, by: "Vegetation" };
      if (y <= g + tree.trunk && d < 0.3) return { s, o, y, by: "Vegetation" };
    }
  }
  return null;
}

/** How far ahead a view stays open, stepping the target out until it is first blocked. */
function sightDistance(world, eye, targetAt, max, stepM) {
  let clear = 0;
  const reach = [];
  for (let d = stepM; d < max; d += stepM) reach.push(d);
  reach.push(max); // the required distance itself, not the last whole step short of it
  for (const d of reach) {
    const target = targetAt(d);
    const block = lineOfSight(world, eye, target);
    if (block) return { available: clear, block, target };
    clear = d;
  }
  return { available: clear, block: null, target: targetAt(clear) };
}

const at = (world, s, o, above) => ({ s, o, y: heightAt(world.al, s, o) + above });

/** Stopping sight distance from a driver in the right lane, approaching the crest. */
export function stoppingSight(world, eyeStation = 100) {
  const eye = at(world, eyeStation, 1.8, STANDARDS.eye);
  const required = STANDARDS.ssdFt / FT;
  const result = sightDistance(
    world,
    eye,
    (d) => at(world, eyeStation + d, 1.8, STANDARDS.object),
    required + 60,
    2
  );
  return { eye, required, ...result, deficient: result.available < required };
}

/** Passing sight distance along the passing zone, station by station. */
export function passingSight(world) {
  const [z0, z1] = SITE.passing;
  const required = STANDARDS.psdFt / FT;
  const stations = [];
  for (let s = z0; s <= z1; s += 8) {
    const eye = at(world, s, 0, STANDARDS.eye);
    const { available } = sightDistance(
      world,
      eye,
      (d) => at(world, s + d, 0, STANDARDS.vehicle),
      required,
      10
    );
    stations.push({ s, available, ok: available >= required });
  }
  // The zone's end, from the last station that still had its passing sight distance.
  let lastOk = -1;
  stations.forEach((st, i) => {
    if (st.ok) lastOk = i;
  });
  const shortFrom = lastOk + 1 < stations.length ? stations[lastOk + 1].s : null;
  const example = stations[stations.length - 3];
  const eye = at(world, example.s, 0, STANDARDS.eye);
  const target = at(world, example.s + required, 0, STANDARDS.vehicle);
  return {
    required,
    zone: z1 - z0,
    stations,
    shortFrom,
    shortLength: shortFrom === null ? 0 : z1 - shortFrom,
    isShort: (s) => shortFrom !== null && s >= shortFrom && s <= z1,
    example: { eye, target, block: lineOfSight(world, eye, target) },
  };
}

/** Intersection sight distance for a left turn from the stop-controlled side road. */
export function intersectionSight(world) {
  const s0 = SITE.intersection;
  // Driver's eye 14.5 ft back from the edge of the travelled way, in the approach lane.
  const eye = at(world, s0 - 1.65, LANE + 4.4, STANDARDS.eye);
  const required = STANDARDS.isdFt / FT;
  const leg = (dir, o) =>
    sightDistance(world, eye, (d) => at(world, s0 + dir * d, o, STANDARDS.vehicle), required, 5);
  return { eye, required, left: leg(-1, 1.8), right: leg(1, -1.8) };
}

/** The curve's safe speed from its radius and superelevation (Green Book eq. 3-8). */
export function curveCheck() {
  const radiusFt = SITE.curve.radius * FT;
  const { superelevation: e, sideFriction: f } = STANDARDS;
  const safeMph = Math.sqrt(15 * radiusFt * (e + f));
  return {
    radiusFt,
    e,
    safeMph,
    postedMph: STANDARDS.speedMph,
    below: safeMph < STANDARDS.speedMph,
  };
}

/** Where the right lane's cross slope and the grade are both under 0.5%: water stays. */
export function flatSpots() {
  const runs = [];
  let run = null;
  for (let s = 0; s <= LENGTH; s += 0.5) {
    const flat = Math.abs(crossSlopeRight(s)) < 0.005 && Math.abs(gradeAt(s)) < 0.005;
    if (flat && !run) run = { from: s, to: s };
    else if (flat) run.to = s;
    else if (run) {
      runs.push({ ...run, length: run.to - run.from, grade: gradeAt(run.from) });
      run = null;
    }
  }
  return { runs, isFlat: (s) => runs.some((r) => s >= r.from && s <= r.to) };
}

/** The low sun ahead once the road leaves the curve, and where it sits in the driver's view. */
export function sunGlare(world) {
  const exit = sample(world.al, 820);
  const azimuth = exit.h + (3 * Math.PI) / 180;
  const elevation = (7 * Math.PI) / 180;
  const inView = [];
  for (let s = 700; s <= LENGTH; s += 2) {
    const h = sample(world.al, s).h;
    const angle = Math.acos(Math.cos(elevation) * Math.cos(azimuth - h));
    if (angle < (10 * Math.PI) / 180) inView.push(s);
  }
  return {
    azimuth,
    elevation,
    offset: azimuth - exit.h,
    from: inView[0],
    to: inView[inView.length - 1],
    length: inView.length ? inView[inView.length - 1] - inView[0] : 0,
  };
}

/** Barrier warrant on the fill: over 10 ft high on a 1V:2H slope, and where rail is missing. */
export function guardrailWarrant() {
  const threshold = STANDARDS.warrantHeightFt / FT;
  const warranted = [];
  for (let s = SITE.embankment[0]; s <= SITE.embankment[1]; s += 1) {
    if (embankment(s) >= threshold) warranted.push(s);
  }
  const [r0, r1] = SITE.guardrail;
  const unprotected = warranted.filter((s) => s < r0 || s > r1);
  const top = Math.max(...warranted.map(embankment));
  // The open stretch past the rail's end, and where its fill is deepest (the middle of it).
  const after = unprotected.filter((s) => s > r1);
  const deepest = after.filter((s) => embankment(s) > top - 0.01);
  return {
    heightFt: top * FT,
    from: warranted[0],
    to: warranted[warranted.length - 1],
    railFt: (r1 - r0) * FT,
    unprotectedFt: unprotected.length * FT,
    gap: after.length
      ? {
          from: after[0],
          to: after[after.length - 1],
          deepest: deepest.length ? deepest[deepest.length >> 1] : after[0],
        }
      : null,
    isWarranted: (s) => embankment(s) >= threshold,
  };
}

/** Cross-sections through the fill, every 15 m. */
export function embankmentSections(world) {
  const sections = [];
  const [a, b] = SITE.embankment;
  for (let s = a + 5; s <= b - 5; s += SITE.sectionsEvery) {
    const line = [];
    for (let o = -12; o <= 32; o += 0.5) line.push({ o, y: heightAt(world.al, s, o) });
    sections.push({ s, line });
  }
  return { sections, everyFt: SITE.sectionsEvery * FT };
}

/** Fixed objects inside the clear zone on the right, over the clear zone stretch. */
export function clearZone(world) {
  const width = STANDARDS.clearZoneFt / FT;
  const [z0, z1] = SITE.clearZone;
  const inside = (o) => o > LANE && o < LANE + width;
  const trees = world.trees.filter((t) => t.s > z0 && t.s < z1 && inside(t.o));
  const poles = world.poles.filter((p) => p.s > z0 && p.s < z1 && inside(p.o));
  return { width, trees, poles, objects: trees.length + poles.length };
}

/** Every analysis, run once on the corridor. */
export function analyse(world) {
  return {
    ssd: stoppingSight(world),
    psd: passingSight(world),
    isd: intersectionSight(world),
    hca: curveCheck(),
    fsa: flatSpots(),
    glr: sunGlare(world),
    grw: guardrailWarrant(),
    emb: embankmentSections(world),
    cz: clearZone(world),
    tsa: { signs: world.signs },
  };
}
